import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Globe, ArrowRight, Info } from 'lucide-react';

interface DomainStep1EnterProps {
  onSubmit: (domain: string) => Promise<void>;
  loading: boolean;
}

export function DomainStep1Enter({ onSubmit, loading }: DomainStep1EnterProps) {
  const [domain, setDomain] = useState('');
  const [error, setError] = useState<string | null>(null);

  const validateDomain = (value: string): boolean => {
    // Basic domain validation
    const domainRegex = /^(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/;
    let cleanDomain = value.toLowerCase().trim();
    cleanDomain = cleanDomain.replace(/^https?:\/\//, '');
    cleanDomain = cleanDomain.replace(/\/$/, '');
    cleanDomain = cleanDomain.replace(/^www\./, '');
    return domainRegex.test(cleanDomain);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!domain.trim()) {
      setError('Please enter a domain');
      return;
    }

    if (!validateDomain(domain)) {
      setError('Please enter a valid domain (e.g., example.com)');
      return;
    }

    await onSubmit(domain);
  };

  return (
    <div className="space-y-6">
      <div className="text-center pb-4">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-4">
          <Globe className="h-8 w-8 text-primary" />
        </div>
        <h2 className="text-2xl font-semibold mb-2">Add Your Custom Domain</h2>
        <p className="text-muted-foreground max-w-md mx-auto">
          Enter the domain you want to use for your white-labeled dashboard.
          You'll need access to your domain's DNS settings.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="max-w-md mx-auto space-y-4">
        <div className="space-y-2">
          <Label htmlFor="domain">Domain Name</Label>
          <Input
            id="domain"
            type="text"
            placeholder="app.yourdomain.com"
            value={domain}
            onChange={(e) => setDomain(e.target.value)}
            disabled={loading}
            className="text-lg"
          />
          {error && (
            <p className="text-sm text-destructive">{error}</p>
          )}
        </div>

        <Alert className="bg-muted/50">
          <Info className="h-4 w-4" />
          <AlertDescription className="text-sm">
            We recommend using a subdomain like <strong>app.yourdomain.com</strong> or{' '}
            <strong>dashboard.yourdomain.com</strong> for your white-labeled dashboard.
          </AlertDescription>
        </Alert>

        <Button 
          type="submit" 
          className="w-full min-h-[44px]"
          disabled={loading || !domain.trim()}
        >
          {loading ? (
            <>
              <span className="animate-spin mr-2">⏳</span>
              Validating...
            </>
          ) : (
            <>
              Continue
              <ArrowRight className="ml-2 h-4 w-4" />
            </>
          )}
        </Button>
      </form>
    </div>
  );
}
